/**
 * Auth Service
 *
 * Handles all authentication-related API calls.
 * Uses the FastAPI-Users endpoints from Sentinel.
 */
import axios from 'axios'
import type {
  User,
  LoginRequest,
  TokenPairResponse,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PortalIdentitySession,
} from '../types/auth'
import { apiHelpers } from './api'

// Auth endpoints use the same base but different path structure
const AUTH_BASE = import.meta.env.DEV ? '/api/v1/auth' : 'https://api.claimshieldpro.com/api/v1/auth'
// A __Host- cookie belongs to the portal origin. The identity API is reached
// through that origin's /api proxy, including in the isolated HTTPS harness.
const PORTAL_OIDC_BASE = '/api/v1/auth/oidc'
export const staffApiOrigin = import.meta.env.VITE_OPS_API_ORIGIN ||
  (import.meta.env.DEV ? '' : 'https://api.claimshieldpro.com')
const STAFF_OIDC_BASE = `${staffApiOrigin}/api/v1/oidc/ops`

/**
 * `fresh: true` asks the sign-in server for a fresh sign-in (`prompt=login`) rather than
 * reusing a sign-in it still remembers: "Use a different account" after a no-access
 * screen, and "Sign in again" after a sign-out that could not end the sign-in server's
 * session (spec b-app-auth-states §3, §5). The server forwards that one value only.
 */
export interface SignInUrlOptions { fresh?: boolean }
/** Why a sign-out happens, when it is not the person's own choice. */
export type LogoutReason = 'denied'
export function signInUrl(base: string, options: SignInUrlOptions = {}): string {
  return options.fresh ? `${base}?prompt=login` : base
}

/**
 * Auth service for authentication operations
 */
export const authService = {
  staffLoginUrl(options?: SignInUrlOptions): string {
    return signInUrl(`${STAFF_OIDC_BASE}/login`, options)
  },

  async staffSession(): Promise<{ person_id: string; csrf_token: string; expires_at: number }> {
    const response = await axios.get<{ person_id: string; csrf_token: string; expires_at: number }>(
      `${STAFF_OIDC_BASE}/session`, { withCredentials: true },
    )
    return response.data
  },

  /** `reason: 'denied'` makes the sign-in server's sign-out land on the no-access screen. */
  async staffLogout(csrf: string, reason?: LogoutReason): Promise<string | null> {
    const response = await axios.post<{ logout_url?: string }>(
      `${STAFF_OIDC_BASE}/logout${reason ? `?reason=${reason}` : ''}`, null,
      { withCredentials: true, headers: { 'X-CSRF-Token': csrf, Accept: 'application/json' } },
    )
    return response.data?.logout_url ?? null
  },

  portalLoginUrl(options?: SignInUrlOptions): string {
    return signInUrl(`${PORTAL_OIDC_BASE}/login`, options)
  },

  async portalSession(): Promise<PortalIdentitySession> {
    const response = await axios.get<PortalIdentitySession>(`${PORTAL_OIDC_BASE}/session`, {
      withCredentials: true,
    })
    return response.data
  },

  async selectPortalCarrier(carrierId: string, csrf: string): Promise<{ carrier_id: string; csrf_token: string }> {
    const response = await axios.post<{ carrier_id: string; csrf_token: string }>(
      `${PORTAL_OIDC_BASE}/select-carrier`, { carrier_id: carrierId },
      { withCredentials: true, headers: { 'X-CSRF-Token': csrf } },
    )
    return response.data
  },

  /** `reason: 'denied'` makes the sign-in server's sign-out land on the no-access screen. */
  async portalLogout(csrf: string, reason?: LogoutReason): Promise<string | null> {
    const response = await axios.post<{ logout_url?: string }>(
      `${PORTAL_OIDC_BASE}/logout${reason ? `?reason=${reason}` : ''}`, null,
      { withCredentials: true, headers: { 'X-CSRF-Token': csrf, Accept: 'application/json' } },
    )
    return response.data?.logout_url ?? null
  },
  /**
   * Login with email/password.
   *
   * Uses /login-with-refresh so we receive a rotating refresh token alongside
   * the access token, enabling silent re-auth when the 60-min access token
   * expires (see api.ts 401 interceptor). The endpoint expects form-urlencoded
   * data for the OAuth2 password flow, same as the standard /login.
   */
  async login(credentials: LoginRequest): Promise<TokenPairResponse> {
    // FastAPI-Users OAuth2 expects form data, not JSON
    const formData = new URLSearchParams()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)

    const response = await axios.post<TokenPairResponse>(
      `${AUTH_BASE}/login-with-refresh`,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    )

    return response.data
  },

  /**
   * Exchange a refresh token for a new (access, refresh) pair.
   * Single-use rotation: the old refresh token is revoked server-side.
   * Uses raw axios (not the shared api instance) so it never re-enters the
   * 401 interceptor that calls this method.
   */
  async refresh(refreshToken: string): Promise<TokenPairResponse> {
    const response = await axios.post<TokenPairResponse>(
      `${AUTH_BASE}/refresh`,
      { refresh_token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return response.data
  },

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<User> {
    const response = await axios.post<User>(`${AUTH_BASE}/register`, data)
    return response.data
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return apiHelpers.get<User>('/v1/users/me')
  },

  /**
   * Update current user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    return apiHelpers.patch<User>('/v1/users/me', data)
  },

  /**
   * Request password reset email
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await axios.post(`${AUTH_BASE}/forgot-password`, data)
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await axios.post(`${AUTH_BASE}/reset-password`, data)
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    await axios.post(`${AUTH_BASE}/verify`, { token })
  },

  /**
   * Request new verification email
   */
  async requestVerification(email: string): Promise<void> {
    await axios.post(`${AUTH_BASE}/request-verify-token`, { email })
  },

  /**
   * Logout (optional - mainly client-side)
   * FastAPI-Users JWT doesn't have server-side logout by default
   */
  async logout(): Promise<void> {
    // If your backend has a logout endpoint, call it here
    // await axios.post(`${AUTH_BASE}/logout`)

    // For JWT, logout is primarily client-side (clear tokens)
    return Promise.resolve()
  },
}
